import type { ViewResponse } from "@bestowinc/tinkertoy-core";
import { LoaderResponseSchema } from "@bestowinc/tinkertoy-core";
import { redirect } from "react-router";

import {
  FAILURE_EVENT_TAGS,
  reportFailureOnce,
} from "../error-telemetry";
import {
  PERFORMANCE_METRIC_NAMES,
  startPerformanceTimer,
} from "../performance-telemetry";
import { ShellLoaderError } from "./shell-loader-error";
import type { ShellConfig } from "./types";

function throwShellLoaderFailure(init: ConstructorParameters<typeof ShellLoaderError>[0]): never {
  const error = new ShellLoaderError(init);
  reportFailureOnce(error, {
    tag: FAILURE_EVENT_TAGS.shellLoader,
    errorCode: error.code,
    message: error.message,
    error,
  });
  throw error;
}

/**
 * Creates a React Router loader that fetches view data from the Tinkertoy server.
 *
 * The loader is the single point of contact between the client and the server.
 * On every navigation, React Router calls this loader with the current URL.
 * The loader fetches the corresponding view data and handles the three possible
 * response types: view (return data), redirect (follow client-side, or
 * full-document navigate when `external`), error (throw {@link ShellLoaderError}).
 *
 * The browser's full URL (origin + path + search) is forwarded as a query
 * parameter so the server can reconstruct the original request context —
 * hostname for host-based flow matching, pathname for path/branch/view
 * resolution, and search params for downstream use.
 *
 * Cancellation is automatic — React Router passes `request.signal`, which aborts
 * the in-flight fetch when a new navigation supersedes the current one.
 */
/**
 * Loader function type for the Tinkertoy shell route. Consumers should use
 * this with React Router's typed `useLoaderData<ShellLoader>()` to get
 * compile-time type safety tied to {@link createShellLoader}'s return type,
 * instead of casting an `unknown`.
 */
export type ShellLoader = ReturnType<typeof createShellLoader>;

export function createShellLoader(config: Pick<ShellConfig, "apiBaseUrl">) {
  return async ({
    request,
  }: {
    request: Request;
  }): Promise<ViewResponse | Response> => {
    const timer = startPerformanceTimer(PERFORMANCE_METRIC_NAMES.loadNavigation, {});

    const serverUrl = `${config.apiBaseUrl}/load?url=${encodeURIComponent(
      request.url,
    )}`;

    let response: Response;
    try {
      response = await fetch(serverUrl, {
        signal: request.signal,
        headers: { Accept: "application/json" },
      });
    } catch (err) {
      // Navigation was superseded — rethrow so React Router can cancel silently.
      if (err instanceof DOMException && err.name === "AbortError") {
        timer.complete("cancelled");
        throw err;
      }
      timer.complete("failure", {
        errorCode: "network_error",
      });
      throwShellLoaderFailure({
        message: err instanceof Error ? err.message : "Network request failed.",
        status: 0,
        code: "network_error",
      });
    }

    const contentType = response.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");

    if (!isJson) {
      timer.complete("failure", {
        errorCode: "non_json_response",
      });
      throwShellLoaderFailure({
        message: response.ok
          ? "Server returned a non-JSON response."
          : response.statusText || `Server returned ${response.status}.`,
        status: response.ok ? 502 : response.status,
        code: "non_json_response",
      });
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      timer.complete("failure", {
        errorCode: "non_json_response",
      });
      throwShellLoaderFailure({
        message: "Server returned malformed JSON.",
        status: 502,
        code: "non_json_response",
      });
    }

    const result = LoaderResponseSchema.safeParse(body);

    if (!result.success) {
      timer.complete("failure", {
        errorCode: "invalid_response_shape",
      });
      throwShellLoaderFailure({
        message: "Unexpected server response format.",
        status: 502,
        code: "invalid_response_shape",
      });
    }

    const parsed = result.data;

    switch (parsed.type) {
      case "redirect":
        if (parsed.external) {
          timer.complete("success");
          // Target lives outside the shell (e.g. a route still served by the
          // legacy v1 host during a migration). A client-side React Router
          // redirect would just re-enter this loader and never resolve, so do a
          // full-document navigation instead, then hang the loader so React
          // Router renders nothing before the browser unloads.
          window.location.assign(parsed.location);
          return await new Promise<never>(() => undefined);
        }
        timer.complete("success");
        return redirect(parsed.location);

      case "error":
        timer.complete("failure", {
          errorCode: parsed.code,
          moduleId: parsed.errorView?.moduleId,
        });
        return throwShellLoaderFailure({
          message: parsed.message,
          status: parsed.status,
          code: parsed.code,
          errorView: parsed.errorView,
          errorContext: parsed.errorContext,
          flowMeta: parsed.flowMeta,
        });

      case "view":
        timer.complete("success", {
          viewId: parsed.loadedView.viewId,
          viewVersion: parsed.loadedView.viewVersion,
        });
        return parsed;
    }
  };
}
